import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import lodash from 'lodash';

import {
  deleteVideofeed,
  getMergeSession,
  fetchVideoFeedVideosAndType,
  getAllVideofeedsWithoutPagination,
  getUserOrganization,
  postCancelMergeSession,
  postCompleteMergeSessionUpload,
  postCreateMergeSession,
  postCreateVideoFeed,
  postFinalizeMergeSession,
  postStartMergeSessionUpload,
  postUploadChunkByPresignedUrl,
} from '../../../../../api';
import { PATHS, TRANSCODING_EVENTS, UPLOAD_TYPE, VIDEOFEED, VIDEOFEED_STATUS, VIDEOFEED_TYPE } from '../../../../../enums';
import { bytesToMB, findSearchValue, getVideoResolution } from '../../../../../helpers';
import UploadFileIcon from '../../../../../assets/icons/upload-icon-file.svg';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { ContainedButton } from '../../../../../components/modules/Buttons';
import { saveClipToStore } from '../../../../../store/videos/videoSlice';
import type { ExtendedVIDEOFEED, TLocalPackage, UploadMode } from '../UploadContentModal';
import { saveMyOrganization } from '../../../../../store/user/userSlice';
import { refetchFeeds } from '../../../../../store/videoFeedsList/videoFeedsListSlice';
import useAwsUploader, { isAwsUploadCancellationRequested } from '../../../../../helpers/useAwsUploader';
import useWorkerDelay from '../../../../../helpers/useWorkerDelay';
import {
  UploadingStatus,
  updateCommonTrigger,
  updateDroppedFilesObject,
  updateUploadProgress,
  updateUploadingContentState,
  updateUploadingFiles,
} from '../../../../../store/sharedStore/sharedStoreSlice';
import styles from './AwsFile.module.scss';

const purgeAxiosCacheKeys = () => {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('axios-cache')) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {}
};

const showPersistentUploadWarning = (message: string) => {
  enqueueSnackbar(message, {
    preventDuplicate: true,
    variant: 'warning',
    persist: true,
    anchorOrigin: { horizontal: 'right', vertical: 'top' },
  });
};

type TAwsFile = TLocalPackage & {
  nextCallBack: (index: number) => void;
  multi_quality: boolean;
  hideModal: () => void;
  originalName?: string;
  loading: boolean;
  cancel: boolean;
  tempId: string;
  index: number;
  onQualityCheckBoxChange: (n: number, q?: boolean) => void;
  setRearrangeDisabled: (b: boolean) => void;
  setDisableDropMore: (b: boolean) => void;
  folderId: string;
  stableKey: (args: { folderId: string; size: number; originalName: string }) => string;
  createOnce: (key: string, fn: () => Promise<any>) => Promise<any>;
  setPackageUploadError: (message: string) => void;
};

const AwsFile: React.FC<TAwsFile> = ({
  onQualityCheckBoxChange,
  setRearrangeDisabled,
  setDisableDropMore,
  upload: toUpload,
  cancel = false,
  multi_quality,
  nextCallBack,
  originalName,
  zipContent,
  singleMov,
  hideModal,
  movFiles,
  loading,
  tempId,
  index,
  name,
  size,
  folderId,
  stableKey,
  createOnce,
  setPackageUploadError,
  ...packageAdditionals
}) => {
  const [videoFeedId, setVideoFeedId] = useState('');
  const [videoFeed, setVideoFeed] = useState<VIDEOFEED>();
  const { uploadingContentState, droppedFilesObject } = useAppSelector((state) => state.sharedStore);
  const { myOrganization } = useAppSelector((state) => state.user);
  const uploadingContentStateRef = useRef(uploadingContentState);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { upload, cancelUploading, localStorageKey, trigger } = useAwsUploader();
  const [overallProgress, setOverallProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState('');
  const uploadErrorRef = useRef('');
  const [appendMergeStarted, setAppendMergeStarted] = useState(false);
  const uploadedBytesRef = useRef(0);
  const uploadInProgressRef = useRef(false);
  const { every } = useWorkerDelay();
  const uploadMode = (packageAdditionals.uploadMode || 'package') as UploadMode;
  const uploadErrorText = uploadError || packageAdditionals.uploadError || '';
  const setUploadLimitError = (message: string) => {
    uploadErrorRef.current = message;
    setUploadError(message);
    setPackageUploadError(message);
  };
  const clearUploadLimitError = () => {
    uploadErrorRef.current = '';
    setUploadError('');
    setPackageUploadError('');
  };
  const nextCallBackUpload = useCallback(() => {
    nextCallBack(index);
  }, [index]);

  useEffect(() => {
    if (localStorageKey) {
      const json = JSON.parse(localStorage.getItem(localStorageKey)!) as ExtendedVIDEOFEED;
      let percentage = 0;

      if (json) {
        uploadedBytesRef.current = Number(json.uploadedBytes || 0);
        percentage = Math.ceil((+json.uploadedBytes / size) * 100);
        if (percentage > 99) {
          percentage = 100;
        }
        setOverallProgress(percentage);
        dispatch(updateUploadProgress(percentage));
        dispatch(updateCommonTrigger());
      }
    }
  }, [trigger, localStorageKey]);

  const [showConfirmUploadButton, setShowConfirmUploadButton] = useState(false);
  const [addMultiQuality, setAddedMultiQuality] = useState(false);
  const handleAdditionalPostUpload = async (addedMultiQuality: boolean) => {
    purgeAxiosCacheKeys();
    const keyStorage = stableKey({ folderId: folderId, size, originalName: originalName! });
    const json = localStorage.getItem(keyStorage);
    let availableToUpload: any;
    let itsSingleVideo = false;
    let uploadType: UPLOAD_TYPE.PACKAGE | UPLOAD_TYPE.STANDALONE = UPLOAD_TYPE.PACKAGE;
    const uploadContentArr = [...uploadingContentState];
    if (json) {
      availableToUpload = { data: JSON.parse(json) };
    } else {
      const videoSizeAndIdArray = movFiles.map((v, i) => {
        let path =
          v.webkitRelativePath.length > 0
            ? v.webkitRelativePath?.split('/')[3]
            : // @ts-ignore
              v.customPath?.split('/')[3];
        if (!path) {
          itsSingleVideo = true;
          path = `${name}_video_single_${i}`;
          uploadType = UPLOAD_TYPE.STANDALONE;
        } else {
          uploadType = UPLOAD_TYPE.PACKAGE;
        }
        if (uploadType === UPLOAD_TYPE.PACKAGE) {
          return {
            video_id: path,
            size: v.size,
          };
        } else {
          return {
            video_id: path,
            size: v.size,
            angle_name: v.name.slice(0, -4),
          };
        }
      });
      for (let i = 0; i < videoSizeAndIdArray.length; i++) {
        const { height: quality, duration } = await getVideoResolution(movFiles[i]);
        // @ts-ignore
        videoSizeAndIdArray[i].quality = quality;
        if (duration) {
          // @ts-ignore
          videoSizeAndIdArray[i].duration = duration;
        }
      }
      const cacheKey = `${folderId}::${size}::${originalName}`;
      availableToUpload = await createOnce(cacheKey, async () => {
        const uploadAttempts = 10;
        let resp: any = null;
        for (let p = 0; p < uploadAttempts; p++) {
          try {
            resp = await postCreateVideoFeed({
              title: name ? name : originalName,
              multi_quality: addedMultiQuality,
              originalName,
              videos: videoSizeAndIdArray,
              upload_type: uploadType,
              folderId: folderId,
            });
            console.log(resp);
            if (resp) {
              clearUploadLimitError();
              // preserve existing resume payload behavior
              const fileListNames = packageAdditionals?.fileListNames ?? [];
              localStorage.setItem(
                keyStorage,
                JSON.stringify({
                  multi_quality: addedMultiQuality,
                  originalName,
                  size,
                  uploadedBytes: 0,
                  fileListNames,
                  ...resp.data,
                }),
              );
              return resp;
            }
          } catch (error: any) {
            console.error(`Attempt ${p + 1} failed:`, error);
            if (error?.response?.data?.message) {
              const message = error.response.data.message;
              setUploadLimitError(message);
              showPersistentUploadWarning(message);
              return null;
            }
          }
          if (p < uploadAttempts - 1) {
            await new Promise((r) => setTimeout(r, 5000));
          }
        }
        return resp;
      });
    }
    if (availableToUpload) {
      uploadContentArr.push({
        ...availableToUpload.data,
        progress: availableToUpload.data?.overallProgress ? availableToUpload.data?.overallProgress : 0,
        status: UploadingStatus.ADDED,
        videofeedTitle: availableToUpload.data.title,
        originalName: originalName,
        videofeedId: availableToUpload.data.id,
        name: availableToUpload.data.title,
      });
      dispatch(updateUploadingContentState(uploadContentArr));
    }
  };

  const APPEND_PART_SIZE = 5 * 1024 * 1024;

  const buildAppendProgressState = (session: any, uploadedBytes: number) => ({
    id: String(session.id),
    videofeedId: String(session.id),
    videofeedTitle: session.title,
    status: UploadingStatus.UPLOADING,
    progress: `${Math.min(100, Math.ceil((uploadedBytes / size) * 100))}`,
    size,
    name: session.title,
    originalName: originalName!,
    videos: session.videos,
    uploadMode: 'append' as const,
    mergeSessionId: String(session.id),
  });

  const updateAppendLocalStorage = (keyStorage: string, session: any, uploadedBytes: number) => {
    const raw = localStorage.getItem(keyStorage);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      parsed.uploadedBytes = uploadedBytes;
      parsed.progress = Math.min(100, Math.ceil((uploadedBytes / size) * 100));
      parsed.videos = session.videos;
      parsed.mergeSessionId = String(session.id);
      parsed.uploadMode = 'append';
      localStorage.setItem(keyStorage, JSON.stringify(parsed));
    } catch {}
  };

  const uploadAppendFile = async (
    session: any,
    sessionVideo: any,
    file: File,
    keyStorage: string,
    uploadedBytesRefForAppend: { current: number },
  ) => {
    const partCount = Math.max(1, Math.ceil(file.size / APPEND_PART_SIZE));
    const startUploadResponse = await postStartMergeSessionUpload(session.id, sessionVideo.id, partCount);
    const uploadId = startUploadResponse.data.uploadId as string;
    const presignedUrls: string[] = startUploadResponse.data.presignedUrls || [];
    const chunks: { PartNumber: number; ETag: string }[] = [];

    for (let partNumber = 1; partNumber <= partCount; partNumber++) {
      const start = (partNumber - 1) * APPEND_PART_SIZE;
      const end = Math.min(start + APPEND_PART_SIZE, file.size);
      const filePart = file.slice(start, end);
      const presignedUrl = presignedUrls[partNumber - 1];
      if (!presignedUrl) {
        throw new Error('Missing append upload URL');
      }
      const res = await postUploadChunkByPresignedUrl(filePart, presignedUrl);
      chunks.push({ PartNumber: partNumber, ETag: res.ETag });
      uploadedBytesRefForAppend.current += filePart.size;
      updateAppendLocalStorage(keyStorage, session, uploadedBytesRefForAppend.current);
      setOverallProgress(Math.min(100, Math.ceil((uploadedBytesRefForAppend.current / size) * 100)));
      dispatch(updateUploadProgress(Math.min(100, Math.ceil((uploadedBytesRefForAppend.current / size) * 100))));
      dispatch(updateCommonTrigger());
    }

    await postCompleteMergeSessionUpload(session.id, sessionVideo.id, { uploadId, chunks });
  };

  const [alreadySelectedQuality, setAlreadySelected] = useState(false);
  useEffect(() => {
    // @ts-ignore
    if (uploadMode === 'append' || loading || packageAdditionals.uploadError) {
      return;
    }

    const uploadContentState = uploadingContentState.filter((item) => item?.uploadMode !== 'additional-angle');

    if (uploadContentState.some((item) => item == null)) {
      return;
    }

    if (uploadContentState.length) {
      const contentExists = uploadContentState.find(
        (f) =>
          f?.originalName === originalName &&
          f.videos?.length === movFiles.length &&
          f.videos?.reduce((a, b) => +a + +b.size, 0) == movFiles?.reduce((a, b) => +a + +b.size, 0),
      );

      if (!contentExists && lodash.isBoolean(myOrganization.allowedVideoQualities) && myOrganization.allowedVideoQualities) {
        setShowConfirmUploadButton(true);
      }

      if (!contentExists && !lodash.isBoolean(myOrganization.allowedVideoQualities)) {
        setShowConfirmUploadButton(true);
      }

      if (!contentExists && !myOrganization?.allowedVideoQualities) {
        setTimeout(() => {
          setRearrangeDisabled(false);
        }, 3000);
        setDisableDropMore(false);
        handleAdditionalPostUpload(false);
      }
    }
  }, [uploadingContentState, uploadMode, loading, packageAdditionals.uploadError]);

  const handleMultiQualitySelection = (b: boolean) => {
    const additionalQualityBoolean = b;
    setTimeout(() => {
      setRearrangeDisabled(false);
    }, 3000);
    setDisableDropMore(false);
    setAddedMultiQuality(additionalQualityBoolean);
    handleAdditionalPostUpload(additionalQualityBoolean);
    setShowConfirmUploadButton(false);
    setAlreadySelected(true);
    onQualityCheckBoxChange(index, additionalQualityBoolean);
  };

  useEffect(() => {
    if (!toUpload || uploadInProgressRef.current) {
      return;
    }
    uploadInProgressRef.current = true;
    if (packageAdditionals.uploadError) {
      nextCallBackUpload();
      return;
    }
    void handleUpload().finally(() => {
      uploadInProgressRef.current = false;
    });
  }, [toUpload, packageAdditionals.uploadTrigger, packageAdditionals.uploadError]);

  const deleteVideoFeedWhenCancel = async (id: string) => {
    const res = await deleteVideofeed({ videofeedId: id, confirm: true });
    getUserOrganization()
      .then((resp) => {
        dispatch(saveMyOrganization(resp.data));
      })
      .catch((error) => console.error(error));
    cancelUploading();
    return res;
  };

  const status = useRef('');
  const showAppendMergeTip = uploadMode === 'append' && appendMergeStarted && status.current === TRANSCODING_EVENTS.STARTED;
  const markCurrentUploadFailed = (feedId?: string) => {
    const failedArr = [...uploadingContentStateRef.current];
    const failedIndex = failedArr.findIndex(
      (item: any) =>
        (feedId && String(item?.videofeedId || item?.id) === String(feedId)) ||
        (item?.originalName === originalName && Array.isArray(item?.videos) && item.videos.length === movFiles?.length),
    );
    if (failedIndex !== -1) {
      failedArr[failedIndex] = { ...failedArr[failedIndex], status: UploadingStatus.FAILED };
      uploadingContentStateRef.current = failedArr;
      dispatch(updateUploadingContentState(failedArr));
    }
    status.current = TRANSCODING_EVENTS.FAILED;
    return {
      hasFailedItem: failedIndex !== -1,
      hasQueuedUpload: failedArr.some((item) => item?.status === UploadingStatus.ADDED),
    };
  };
  const failCurrentUpload = (feedId?: string) => {
    if (isAwsUploadCancellationRequested()) return;
    const { hasFailedItem, hasQueuedUpload } = markCurrentUploadFailed(feedId);
    if (hasQueuedUpload || !hasFailedItem) {
      nextCallBackUpload();
    }
  };

  const handleStatus = async () => {
    if (!videoFeedId) {
      return;
    }

    try {
      const res = await fetchVideoFeedVideosAndType(videoFeedId);
      console.log(res);
      setVideoFeed(res.data);
      return res;
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    uploadingContentStateRef.current = uploadingContentState;

    const uploadingContent = uploadingContentState.find(
      (x) => x?.uploadMode !== 'additional-angle' && x?.videofeedId === videoFeedId,
    );
    if (uploadingContent) {
      switch (uploadingContent.status) {
        case UploadingStatus.TRANSCODING:
          if (status.current !== TRANSCODING_EVENTS.COMPLETED && status.current !== TRANSCODING_EVENTS.FAILED) {
            status.current = TRANSCODING_EVENTS.STARTED;
          }
          break;
        case UploadingStatus.FINISHED:
          status.current = TRANSCODING_EVENTS.COMPLETED;
          if (!videoFeed && videoFeedId) {
            void handleStatus();
          }
          getUserOrganization()
            .then((resp) => {
              dispatch(saveMyOrganization(resp.data));
            })
            .catch((error) => console.error(error));
          break;
        case UploadingStatus.FAILED:
          status.current = TRANSCODING_EVENTS.FAILED;
          break;
        default:
          break;
      }
    }
  }, [uploadingContentState]);

  // Post-upload status polling driven by a Web Worker interval (not throttled in background tabs)
  useEffect(() => {
    if (uploadMode === 'append' || overallProgress !== 100) return;
    let stop: null | (() => void) = null;

    const tick = async () => {
      const response = await handleStatus();
      if (
        response?.data?.status === VIDEOFEED_STATUS.TRANSCODING &&
        status.current !== TRANSCODING_EVENTS.COMPLETED &&
        status.current !== TRANSCODING_EVENTS.FAILED
      ) {
        status.current = TRANSCODING_EVENTS.STARTED;
      }
      if (response?.data?.status === VIDEOFEED_STATUS.DONE) {
        status.current = TRANSCODING_EVENTS.COMPLETED;
        if (stop) {
          stop();
          stop = null;
        }
      } else if (response?.data.status === VIDEOFEED_STATUS.TRANSCODING_FAILED) {
        if (stop) {
          stop();
          stop = null;
        }
      }
    };

    // run immediately, then every 20s via worker
    tick();
    stop = every(20000, tick);
    return () => {
      if (stop) stop();
    };
  }, [overallProgress, uploadMode]);

  useEffect(() => {
    const urlFolderId = findSearchValue('folderId');
    const keyStorage = stableKey({ folderId: folderId ? folderId : urlFolderId!, size, originalName: originalName! });
    const wasAlreadyUploading = localStorage.getItem(keyStorage);
    if (wasAlreadyUploading) {
      const parsedUpload = JSON.parse(wasAlreadyUploading);
      uploadedBytesRef.current = Number(parsedUpload?.uploadedBytes || 0);
      dispatch(updateDroppedFilesObject([...droppedFilesObject, wasAlreadyUploading]));
      setOverallProgress(+parsedUpload?.overallProgress > 0 ? +parsedUpload?.overallProgress : 0);
      dispatch(updateUploadingFiles({ keyStorage, data: parsedUpload }));
    } else {
      dispatch(updateUploadingFiles({ keyStorage }));
    }
    if (status.current === TRANSCODING_EVENTS.COMPLETED || status.current === TRANSCODING_EVENTS.FAILED) {
      handleStatus();
    }
  }, [status]);

  const handleAppendUpload = async () => {
    purgeAxiosCacheKeys();
    const keyStorage = stableKey({ folderId, size, originalName: originalName! });
    const sortedMovFiles = [...movFiles].sort((a, b) => a.name.localeCompare(b.name));
    const uploadedBytesRefForAppend = { current: 0 };
    let appendSessionId = '';
    let advancedQueue = false;

    const advanceQueueOnce = () => {
      if (advancedQueue) {
        return;
      }
      advancedQueue = true;
      nextCallBackUpload();
    };

    try {
      const { data } = await getUserOrganization();
      if (data && +data?.hours_limit_used >= +data?.hours_limit) {
        showPersistentUploadWarning('Hours limit reached');
        advanceQueueOnce();
        return;
      }

      const appendVideos = await Promise.all(
        sortedMovFiles.map(async (file) => {
          const { height: quality, duration } = await getVideoResolution(file);
          return {
            name: file.name,
            size: file.size,
            quality,
            duration,
          };
        }),
      );

      const sessionResponse = await postCreateMergeSession({
        folderId: Number(folderId),
        title: name ? name : originalName!,
        videos: appendVideos,
      });
      const session = sessionResponse.data;
      appendSessionId = String(session.id);

      const pendingState = {
        ...buildAppendProgressState(session, 0),
        uploadedBytes: 0,
        fileListNames: sortedMovFiles.map((file) => file.name),
        status: UploadingStatus.UPLOADING,
      } as any;

      localStorage.setItem(keyStorage, JSON.stringify(pendingState));

      const uploadArr = [...uploadingContentStateRef.current];
      uploadArr.push(pendingState);
      dispatch(updateUploadingContentState(uploadArr));

      setVideoFeedId(appendSessionId);
      setOverallProgress(0);
      dispatch(updateUploadProgress(0));
      dispatch(updateCommonTrigger());

      for (let i = 0; i < session.videos.length; i++) {
        await uploadAppendFile(session, session.videos[i], sortedMovFiles[i], keyStorage, uploadedBytesRefForAppend);
      }

      const transcodeState = {
        ...pendingState,
        uploadedBytes: uploadedBytesRefForAppend.current,
        progress: '100',
        status: UploadingStatus.TRANSCODING,
      } as any;
      localStorage.setItem(keyStorage, JSON.stringify(transcodeState));
      const transcodeArr = [...uploadingContentStateRef.current];
      const transcodeIndex = transcodeArr.findIndex((item) => String(item?.mergeSessionId || item?.videofeedId) === appendSessionId);
      if (transcodeIndex !== -1) {
        transcodeArr.splice(transcodeIndex, 1, transcodeState);
        dispatch(updateUploadingContentState(transcodeArr));
      }
      status.current = TRANSCODING_EVENTS.STARTED;
      setAppendMergeStarted(true);

      await postFinalizeMergeSession(appendSessionId, name ? name : originalName!);
      dispatch(refetchFeeds());
      advanceQueueOnce();

      const startedAt = Date.now();
      const maxWaitTime = 2 * 60 * 60 * 1000;
      let finalVideofeedId = '';
      let mergeSessionDone = false;
      while (Date.now() - startedAt < maxWaitTime) {
        const mergeSessionResponse = await getMergeSession(appendSessionId);
        finalVideofeedId = mergeSessionResponse.data?.final_videofeed?.id
          ? String(mergeSessionResponse.data.final_videofeed.id)
          : finalVideofeedId;
        if (mergeSessionResponse.data?.status === 'DONE' && finalVideofeedId) {
          mergeSessionDone = true;
          break;
        }
        if (mergeSessionResponse.data?.status === 'FAILED') {
          throw new Error('Append processing failed');
        }
        await new Promise((resolve) => setTimeout(resolve, 15000));
      }

      if (!mergeSessionDone || !finalVideofeedId) {
        throw new Error('Append processing timed out');
      }

      const finalVideoFeedResponse = await fetchVideoFeedVideosAndType(finalVideofeedId);
      const finalVideoFeed = finalVideoFeedResponse.data;
      setVideoFeed(finalVideoFeed);
      setVideoFeedId(finalVideofeedId);
      status.current = TRANSCODING_EVENTS.COMPLETED;

      const finishedState = {
        ...transcodeState,
        id: finalVideofeedId,
        videofeedId: finalVideofeedId,
        videofeedTitle: finalVideoFeed.title,
        status: UploadingStatus.FINISHED,
        videos: finalVideoFeed.videos,
      } as any;
      localStorage.removeItem(keyStorage);

      const finishedArr = [...uploadingContentStateRef.current];
      const finishedIndex = finishedArr.findIndex(
        (item) => item?.originalName === originalName && String(item?.mergeSessionId || item?.videofeedId) === appendSessionId,
      );
      if (finishedIndex !== -1) {
        finishedArr.splice(finishedIndex, 1, finishedState);
        dispatch(updateUploadingContentState(finishedArr));
      }

      getUserOrganization()
        .then((resp) => {
          dispatch(saveMyOrganization(resp.data));
        })
        .catch((error) => console.error(error));
      dispatch(refetchFeeds());
      return true;
    } catch (error) {
      console.error(error);
      const message = (error as any)?.response?.data?.message;
      if (message) {
        setUploadLimitError(message);
        showPersistentUploadWarning(message);
      }
      try {
        if (appendSessionId) {
          await postCancelMergeSession(appendSessionId);
        }
      } catch {}
      localStorage.removeItem(keyStorage);
      const failedState = {
        ...buildAppendProgressState(
          { id: appendSessionId || '', title: name ? name : originalName!, videos: sortedMovFiles },
          uploadedBytesRefForAppend.current,
        ),
        uploadedBytes: uploadedBytesRefForAppend.current,
        status: UploadingStatus.FAILED,
        fileListNames: sortedMovFiles.map((file) => file.name),
      } as any;
      const failedArr = [...uploadingContentStateRef.current];
      const failedIndex = failedArr.findIndex(
        (item) => item?.originalName === originalName && String(item?.mergeSessionId || item?.videofeedId) === appendSessionId,
      );
      if (failedIndex !== -1) {
        failedArr.splice(failedIndex, 1, failedState);
        dispatch(updateUploadingContentState(failedArr));
      }
      status.current = TRANSCODING_EVENTS.FAILED;
      advanceQueueOnce();
      return false;
    }
  };

  const handleUpload = async () => {
    if (uploadMode === 'append') {
      await handleAppendUpload();
      return;
    }

    purgeAxiosCacheKeys();
    let itsSingleVideo = false;
    let uploadType: UPLOAD_TYPE.PACKAGE | UPLOAD_TYPE.STANDALONE = UPLOAD_TYPE.PACKAGE;
    let currentVideofeedId = '';
    try {
      const videoSizeAndIdArray = movFiles.map((v, i) => {
        let path =
          v.webkitRelativePath.length > 0
            ? v.webkitRelativePath?.split('/')[3]
            : // @ts-ignore
              v.customPath?.split('/')[3];
        if (!path) {
          itsSingleVideo = true;
          path = `${name}_video_single_${i}`;
          uploadType = UPLOAD_TYPE.STANDALONE;
        } else {
          uploadType = UPLOAD_TYPE.PACKAGE;
        }
        return uploadType === UPLOAD_TYPE.PACKAGE
          ? { video_id: path, size: v.size }
          : { video_id: path, size: v.size, angle_name: v.name.slice(0, -4) };
      });

      const keyStorage = stableKey({
        folderId: folderId,
        size,
        originalName: originalName!,
      });
      let availableToUpload: any;
      // @ts-ignore
      const json = localStorage.getItem(keyStorage);

      if (json) {
        availableToUpload = { data: JSON.parse(json) };
      } else {
        // EARLY EXIT #2: parent already created and pushed to state — no POST
        const existsInState = uploadingContentStateRef.current.find(
          (x: any) =>
            x?.uploadMode !== 'additional-angle' &&
            x?.originalName === originalName &&
            Array.isArray(x?.videos) &&
            x?.videos?.length === movFiles.length &&
            (x?.videofeedId || x?.id),
        );
        if (existsInState) {
          availableToUpload = { data: existsInState };
        }
      }

      if (!availableToUpload) {
        // inject precomputed meta from onSubmit (if present)
        if (packageAdditionals?.movMeta?.length === videoSizeAndIdArray.length) {
          for (let i = 0; i < videoSizeAndIdArray.length; i++) {
            // @ts-ignore
            videoSizeAndIdArray[i].quality = packageAdditionals.movMeta[i].quality;
            if (typeof packageAdditionals.movMeta[i].duration === 'number') {
              // @ts-ignore
              videoSizeAndIdArray[i].duration = packageAdditionals.movMeta[i].duration;
            }
          }
        } else {
          for (let i = 0; i < videoSizeAndIdArray.length; i++) {
            const { height: quality, duration } = await getVideoResolution(movFiles[i]);
            // @ts-ignore
            videoSizeAndIdArray[i].quality = quality;
            if (duration) {
              // @ts-ignore
              videoSizeAndIdArray[i].duration = duration;
            }
          }
        }

        const cacheKey = `${folderId!}::${size}::${originalName}`;
        const resp = await createOnce(cacheKey, async () => {
          const uploadAttempts = 10;
          let r: any = null;

          for (let p = 0; p < uploadAttempts; p++) {
            try {
              r = await postCreateVideoFeed({
                folderId: folderId,
                videos: videoSizeAndIdArray,
                upload_type: uploadType,
                title: name ? name : originalName,
                multi_quality,
                originalName,
              });
              if (r) {
                clearUploadLimitError();
                const fileListNames = packageAdditionals?.fileListNames ?? [];
                localStorage.setItem(
                  keyStorage,
                  JSON.stringify({
                    ...r.data,
                    size,
                    originalName,
                    uploadedBytes: 0,
                    multi_quality,
                    fileListNames,
                  }),
                );
                return r;
              }
            } catch (error: any) {
              console.error(`Attempt ${p + 1} failed:`, error);
              if (error?.response?.data?.message) {
                const message = error.response.data.message;
                setUploadLimitError(message);
                showPersistentUploadWarning(message);
                return null; // stop retrying on server-declared error
              }
            }
            if (p < uploadAttempts - 1) await new Promise((res) => setTimeout(res, 5000));
          }
          return r;
        });

        if (!resp) {
          setUploadLimitError(uploadErrorRef.current || 'This package cannot be uploaded because the organisation limit was exceeded.');
          failCurrentUpload();
          return;
        }
        availableToUpload = resp;
      }

      // from here on, safe to use:
      const { videos, id } = availableToUpload.data;
      currentVideofeedId = id;

      setVideoFeedId(id);
      const uploadingContents = [...uploadingContentStateRef.current];
      // @ts-ignore
      const indexOfContent = uploadingContents.findIndex(
        // @ts-ignore
        (x) =>
          x?.uploadMode !== 'additional-angle' &&
          x?.originalName === originalName &&
          Array.isArray(x?.videos) &&
          x.videos.length === movFiles?.length,
      );
      const pkg = uploadingContents[indexOfContent];

      if (indexOfContent !== -1) {
        uploadingContents.splice(indexOfContent, 1, {
          ...pkg,
          videofeedId: id,
          videofeedTitle: name,
          status: UploadingStatus.UPLOADING,
        });
      } else {
        uploadingContents.push({
          ...availableToUpload.data,
          id: String(id),
          videofeedId: String(id),
          videofeedTitle: availableToUpload.data.title || name,
          originalName: originalName!,
          name: availableToUpload.data.title || name,
          size,
          status: UploadingStatus.UPLOADING,
          uploadMode,
        });
      }
      uploadingContentStateRef.current = uploadingContents;
      dispatch(updateUploadingContentState(uploadingContents));

      // this is for silmultaneous upload of videofiles in one package
      // await Promise.all(
      //   videos.map((video: any, index: number) => {
      //     return upload({ video_id: video.id, file: movFiles[index], keyStorage, videofeedId: id });
      //   }),
      // );

      // this for current uploading logic, where videos files are uploaded one by one in one package
      let success = false;

      const { data } = await getUserOrganization();
      if (data) {
        if (+data?.hours_limit_used >= +data?.hours_limit) {
          showPersistentUploadWarning('Hours limit reached');
          failCurrentUpload(id);
          return;
        }
      }
      if (!itsSingleVideo) {
        try {
          success = await upload({
            file: zipContent,
            videofeedId: id,
            itsZip: true,
            keyStorage,
          });
        } catch (err) {
          if (isAwsUploadCancellationRequested()) return;
          failCurrentUpload(id);
          await handleStatus();
          localStorage.removeItem(keyStorage);
          return;
        }
        if (!success) {
          failCurrentUpload(id);
          return;
        }
      }

      for (let i = 0; i < videos.length; i++) {
        let video = videos[i];
        try {
          success = await upload({ video_id: video.id, file: movFiles[i], keyStorage, videofeedId: id });
        } catch (err) {
          if (isAwsUploadCancellationRequested()) return;
          console.warn(err);
          success = false;
        }
        if (!success) {
          failCurrentUpload(id);
          return;
        }
      }
      if (success) {
        localStorage.removeItem(keyStorage);
      }
      nextCallBackUpload();
    } catch (err) {
      if (isAwsUploadCancellationRequested()) return;
      failCurrentUpload(currentVideofeedId);
      console.warn(err);
    }
  };

  const getRedirectFeed = async () => {
    if (!videoFeed) return null;
    const resolvedFolderId = videoFeed?.folder?.id ? videoFeed?.folder?.id : videoFeed?.folder_id;

    if (!resolvedFolderId || videoFeed.type !== VIDEOFEED_TYPE.TIMELINE) {
      return videoFeed;
    }

    try {
      const groupedFeeds = await getAllVideofeedsWithoutPagination(String(resolvedFolderId));
      const groupedFeed = groupedFeeds?.documents?.find((feed: VIDEOFEED) => String(feed.id) === String(videoFeed.id));
      const referenceTimeline =
        groupedFeed?.reference_timelines?.find((feed: VIDEOFEED) => String(feed.id) !== String(videoFeed.id)) ||
        groupedFeed?.reference_timelines?.[0];

      if (referenceTimeline && !(groupedFeed?.topics?.length || 0)) {
        return referenceTimeline;
      }
    } catch (error) {
      console.warn('Failed to resolve upload redirect feed', error);
    }

    return videoFeed;
  };

  const redirect = async () => {
    const parentPage = findSearchValue('parentPage');
    const targetFeed = await getRedirectFeed();
    if (targetFeed) {
      dispatch(saveClipToStore(targetFeed.videos[0]));
      history.push(
        `${targetFeed.type === VIDEOFEED_TYPE.TIMELINE ? PATHS.TIMELINE.path : PATHS.COMPOSER_PAGE.path}?feedId=${targetFeed.id}&folderId=${
          targetFeed?.folder?.id ? targetFeed?.folder?.id : targetFeed?.folder_id || videoFeed?.folder?.id || videoFeed?.folder_id
        }&fileside=true&feed=0&clipId=${targetFeed.videos[0].id}&clip=0&pageType=${targetFeed.type}${
          parentPage ? `&parentPage=${parentPage}` : ''
        }`,
      );
      hideModal();
    }
  };

  return (
    <div className={styles.wrapper} onClick={status.current === TRANSCODING_EVENTS.COMPLETED ? redirect : () => {}}>
      {/* alreadySelectedQuality = {`${alreadySelectedQuality}`} <br />
      showConfirmUploadButton = {`${showConfirmUploadButton}`} <br />
      myOrganization = {`${myOrganization?.allowedVideoQualities}`} */}
      {!alreadySelectedQuality && showConfirmUploadButton && myOrganization?.allowedVideoQualities && uploadMode !== 'append' && (
        <div className={styles.additionalOverlay}>
          {' '}
          <div className={styles.overlayTitle}>Add additional multi qualities to ({name ? name : originalName})?</div>
          <div className={styles.overlayInner}>
            <ContainedButton style={{ marginRight: '8px' }} className={styles.overlayBtn} onClick={() => handleMultiQualitySelection(true)}>
              {' '}
              Yes{' '}
            </ContainedButton>
            <ContainedButton className={styles.overlayBtn} onClick={() => handleMultiQualitySelection(false)}>
              {' '}
              No{' '}
            </ContainedButton>
          </div>
        </div>
      )}
      {uploadErrorText && <div className={styles.uploadError}>{uploadErrorText}</div>}
      <Tooltip
        title={`${name}: ${bytesToMB(size)} MB ${
          status.current === TRANSCODING_EVENTS.COMPLETED ? 'Videofeed is ready, click to proceed' : ''
        }`}
        key={name}
        placement="left-end"
      >
        <div key={name} className={styles.file}>
          {!singleMov ? (
            <>
              <img className={styles.fileIcon} src={UploadFileIcon} alt="upload content" />
              <div className={styles.rightSide}>
                <div className={styles.fileName}>{name}</div>
                <div className={styles.statusStack}>
                  {status.current ? (
                    <Tooltip title={status.current}>
                      <div className={styles.status}>
                        {status.current === TRANSCODING_EVENTS.STARTED && 'Processing...'}
                        {status.current === TRANSCODING_EVENTS.COMPLETED && !videoFeed && (
                          <CircularProgress size={20} color="inherit" sx={{ marginRight: '5px' }} />
                        )}
                        {status.current === TRANSCODING_EVENTS.COMPLETED && videoFeed && <span className={styles.redirect}>Complete</span>}
                        {status.current === TRANSCODING_EVENTS.FAILED && (
                          <span className={styles.redirect} style={{ color: 'red' }}>
                            Failed
                          </span>
                        )}
                      </div>
                    </Tooltip>
                  ) : (
                    lodash.isNumber(overallProgress) && (
                      <div className={styles.progress}>
                        {loading && (
                          <CircularProgress
                            size={20}
                            variant="determinate"
                            color="inherit"
                            value={overallProgress}
                            sx={{ marginRight: '5px' }}
                          />
                        )}
                        {overallProgress}%
                      </div>
                    )
                  )}
                  {showAppendMergeTip && <div className={styles.mergeTip}>Merged video is processing in Videofeed List.</div>}
                </div>
              </div>
            </>
          ) : (
            <div className={styles.movList}>
              {movFiles.map((f, i) => (
                <div key={`${i}_file`} className={styles.movFile}>
                  <img className={styles.fileIcon} src={UploadFileIcon} alt="upload content" />
                  <div className={styles.rightSide}>
                    <div className={styles.fileName}>
                      {
                        // @ts-ignore
                        f.name || f.customPath
                      }
                    </div>
                    <div className={styles.statusStack}>
                      {status.current ? (
                        <Tooltip title={status.current}>
                          <div className={styles.status}>
                            {status.current === TRANSCODING_EVENTS.STARTED && 'Processing...'}
                            {status.current === TRANSCODING_EVENTS.COMPLETED && !videoFeed && (
                              <CircularProgress size={20} color="inherit" sx={{ marginRight: '5px' }} />
                            )}
                            {status.current === TRANSCODING_EVENTS.COMPLETED && videoFeed && (
                              <span className={styles.redirect}>Complete</span>
                            )}
                            {status.current === TRANSCODING_EVENTS.FAILED && (
                              <span className={styles.redirect} style={{ color: 'red' }}>
                                Failed
                              </span>
                            )}
                          </div>
                        </Tooltip>
                      ) : (
                        lodash.isNumber(overallProgress) && (
                          <div className={styles.progress}>
                            {loading && (
                              <CircularProgress
                                size={20}
                                variant="determinate"
                                color="inherit"
                                value={overallProgress}
                                sx={{ marginRight: '5px' }}
                              />
                            )}
                            {overallProgress}%
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Tooltip>
    </div>
  );
};

export default AwsFile;
